/**
 * Onboarding · Step 1 — The Desk (welcome card on the canvas).
 *
 * Mirrors the home-page `OnboardingWelcomeDialog` ("Open your first
 * canvas") visually so the user moves from one welcome card to the
 * next without a jarring shift in design language. Same left-aligned
 * stack — eyebrow + heading + lede + a primary rust pill CTA — and the
 * same Cohesive mark sits to the right of the body as a quiet brand
 * anchor (no drag interaction; the home card has none either).
 *
 * Why no more drag-into-slot gesture:
 *   The earlier iteration used a literal drag-into-slot composition as
 *   a self-teaching gesture for the canvas's drag-as-direct-manipulation
 *   model. Steps 2 and 3 already teach that gesture authentically with
 *   real explorer-to-canvas drops and Note placement, so the synthetic
 *   slot is redundant. Aligning step 1 with the home dialog also makes
 *   the welcome arc feel like one continuous moment rather than two
 *   different brand visits.
 *
 * Behaviour:
 *   • Single primary CTA "Begin tour" advances to step 2 via `onAdvance`.
 *   • Button click plays the same celebratory pop+check+sonar beat as
 *     the final step's Finish button, then advances — that's a deliberate
 *     bookend (open with celebration, close with celebration).
 *   • No auto-detect, no Loro writes — the orchestrator handles the
 *     `started` flag on advance.
 */

import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Flourish, StepHeader } from "../shared/StepHeader";
import type { StepProps } from "../shared/types";

// ── Typewriter heading ──────────────────────────────────────────────────────
// "A canvas for " is plain; "humans and agents." renders in Flourish (italic
// Instrument Serif). Characters are revealed one-by-one via setInterval.
// Respects prefers-reduced-motion: shows full text immediately when set.

const PLAIN_PREFIX = "A visual canvas for ";
const FLOURISH_SUFFIX = "all of your context.";
const FULL_LENGTH = PLAIN_PREFIX.length + FLOURISH_SUFFIX.length;
const CHAR_INTERVAL_MS = 32;
const START_DELAY_MS = 260;

function usePrefersReducedMotion(): boolean {
  const [reduced] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  return reduced;
}

const TypewriterHeading = memo(function TypewriterHeading() {
  const reducedMotion = usePrefersReducedMotion();
  const [visibleCount, setVisibleCount] = useState(
    reducedMotion ? FULL_LENGTH : 0,
  );

  useEffect(() => {
    if (reducedMotion) return;
    let count = 0;
    let interval: ReturnType<typeof setInterval>;
    const timeout = window.setTimeout(() => {
      interval = setInterval(() => {
        count += 1;
        setVisibleCount(count);
        if (count >= FULL_LENGTH) clearInterval(interval);
      }, CHAR_INTERVAL_MS);
    }, START_DELAY_MS);
    return () => {
      window.clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [reducedMotion]);

  const plainVisible = Math.min(visibleCount, PLAIN_PREFIX.length);
  const flourishVisible = Math.max(0, visibleCount - PLAIN_PREFIX.length);

  return (
    <>
      {PLAIN_PREFIX.slice(0, plainVisible)}
      {flourishVisible > 0 && (
        <Flourish>{FLOURISH_SUFFIX.slice(0, flourishVisible)}</Flourish>
      )}
    </>
  );
});

/**
 * Total length of the open-tour celebration before we call `onAdvance`.
 * Mirrors the FINISH_BEAT_MS in KeepExploringStep so the open and close
 * beats feel matched — same pop, same check draw, same sonar.
 */
const BEGIN_BEAT_MS = 520;

export const DragMarkStep = memo(function DragMarkStep({
  onAdvance,
}: StepProps) {
  const onAdvanceRef = useRef(onAdvance);
  onAdvanceRef.current = onAdvance;

  const [beginning, setBeginning] = useState(false);

  const handleBegin = useCallback(() => {
    if (beginning) return;
    setBeginning(true);
    window.setTimeout(() => onAdvanceRef.current(), BEGIN_BEAT_MS);
  }, [beginning]);

  // Keyboard affordance — Enter/Space anywhere on the card advances.
  // Matches the home-dialog's autofocus-on-primary pattern in spirit
  // without us needing to imperatively focus across a portal boundary.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Enter") return;
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
      // Don't hijack typing in an input / textarea / contenteditable.
      if (tag === "input" || tag === "textarea") return;
      const target = e.target as HTMLElement | null;
      if (target?.isContentEditable) return;
      e.preventDefault();
      handleBegin();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleBegin]);

  return (
    <div
      style={{
        flex: "1 1 auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "var(--co-onb-stack-md)",
        width: "100%",
        minWidth: 0,
      }}
    >
      <StepHeader
        heading={<TypewriterHeading />}
        headingClassName=""
        lede={
          <>
            Cohesive turns a visual chat into a spatial workspace for you and
            your agent.
            <br />
            <br />
            Let's get started.
          </>
        }
      />

      <div
        className="_co_onb_settle_4"
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "flex-end",
          alignSelf: "flex-end",
          marginTop: "var(--co-onb-stack-sm)",
        }}
      >
        <button
          type="button"
          className={`nodrag _co_onb_finish${beginning ? " _co_onb_finish_pop" : ""}`}
          onClick={handleBegin}
          onPointerDown={(e) => e.stopPropagation()}
          disabled={beginning}
          aria-label="Begin canvas tour"
          style={{
            position: "relative",
            minHeight: 40,
            paddingLeft: 20,
            paddingRight: 20,
            fontSize: "var(--co-onb-fs-cta)",
            gap: 8,
          }}
        >
          {beginning ? "Let's go" : "Begin tour"}
          {!beginning && <Arrow />}
        </button>

        {beginning && (
          <>
            <span aria-hidden className="_co_onb_finish_ring" />
            <span
              aria-hidden
              className="_co_onb_finish_ring _co_onb_finish_ring_late"
            />
          </>
        )}
      </div>
    </div>
  );
});

const Arrow = memo(function Arrow() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden
      style={{ transform: "translateY(0.5px)" }}
    >
      <path
        d="M3 7h8m0 0L7.5 3.5M11 7l-3.5 3.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
});
